import { ReactNode } from "react";
import { Controller, Control, FieldValues, Path } from "react-hook-form";

import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteMulti,
  AutocompleteMultiProps,
} from "src/components/Autocomplete";

export type { AutocompleteProps, AutocompleteMultiProps };
export { Autocomplete, AutocompleteMulti };

// ============= Single RHF Field =============

export interface AutocompleteFieldProps<T, TForm extends FieldValues>
  extends Omit<AutocompleteProps<T>, "value" | "onChange" | "onBlur"> {
  name: Path<TForm>;
  control: Control<TForm, any, any>;
  /** Called after the field value is set — use for side effects like resetting related fields. */
  onChangeEffect?: (value: T | null) => void;
}

export function AutocompleteField<T, TForm extends FieldValues>({
  name,
  control,
  onChangeEffect,
  ...baseProps
}: AutocompleteFieldProps<T, TForm>): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...baseProps}
          value={field.value ?? null}
          onChange={(value) => {
            field.onChange(value);
            onChangeEffect?.(value);
          }}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

// ============= Multi RHF Field =============

export interface AutocompleteMultiFieldProps<T, TForm extends FieldValues>
  extends Omit<AutocompleteMultiProps<T>, "value" | "onChange" | "onBlur"> {
  name: Path<TForm>;
  control: Control<TForm, any, any>;
  onChangeEffect?: (value: T[]) => void;
}

export function AutocompleteMultiField<T, TForm extends FieldValues>({
  name,
  control,
  onChangeEffect,
  ...baseProps
}: AutocompleteMultiFieldProps<T, TForm>): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <AutocompleteMulti
          {...baseProps}
          value={field.value ?? []}
          onChange={(value) => {
            field.onChange(value);
            onChangeEffect?.(value);
          }}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}
